@if (impactReport) {
  <app-page-header [breadcrumbs]="breadcrumbs" />

  <div class="py-4">
    <!-- Container blanc avec même largeur que les enfants -->
    <div
      id="container-white"
      class="mx-auto rounded-lg bg-white pb-8 md:max-w-5xl lg:max-w-7xl xl:max-w-[1400px] 2xl:max-w-[1800px]"
    >
      <div
        class="mb-6 h-[260px] w-full overflow-hidden rounded-lg bg-greyscale-200"
      >
        @if (impactReport.trip.imagePath) {
          <img
            [src]="impactReport.trip.imagePath"
            [alt]="impactReport.trip.title || 'Voyage'"
            class="h-full w-full object-cover"
          />
        } @else {
          <!-- Placeholder sobre -->
          <div class="flex h-full items-center justify-center pb-12">
            <app-greentripper-pin-icon
              styleClass="w-12 h-12 text-greyscale-400 ml-[7px]"
            />
          </div>
        }
      </div>

      <!-- Card Header et tous les composants - Largeur réduite de 30px de chaque côté -->
      <div class="space-y-8 px-2 md:px-6 xl:px-[36px]">
        <!-- Header Card -->
        <app-trip-impact-header
          [trip]="impactReport.trip"
          [countries]="impactReport.countries || []"
          [hasContributed]="false"
          [clients]="
            packLimitationService.canFilterClient()
              ? impactReport.clients || []
              : []
          "
          [logoSources]="getLogoSources()"
        >
        </app-trip-impact-header>

        <!-- Section Aperçu Général -->
        <app-impact-overview
          [tripImpact]="impactReport.trip.tripImpact || null"
          [fullImpactDetails]="impactReport.fullImpactDetails"
          [tripName]="impactReport.trip.title || ''"
          [tripId]="impactReport.trip.id || ''"
          [travelers]="impactReport.trip.travellers || 1"
          [company]="impactReport.company || null"
          [clients]="
            packLimitationService.canFilterClient()
              ? impactReport.clients || []
              : []
          "
          [showPercentage]="showPercentage"
          [excludeMainTransport]="excludeMainTransport"
          [groupTransports]="groupTransports"
          (showPercentageChange)="onShowPercentageChange($event)"
          (excludeMainTransportChange)="onExcludeMainTransportChange($event)"
          (groupTransportsChange)="onGroupTransportsChange($event)"
        >
        </app-impact-overview>

        <!-- Section Statistiques -->
        <section class="space-y-6">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-4">
              <h2 class="text-2xl font-bold text-[#293834]">
                {{ t("impact.stats.title") || "Statistiques" }}
              </h2>
              <!-- SelectButton pour mode Groupe/Individuel -->
              <div class="flex items-center gap-2">
                <p-selectButton
                  [options]="viewModeOptions"
                  [(ngModel)]="impactService.viewMode"
                  optionLabel="label"
                  optionValue="value"
                  [allowEmpty]="false"
                  styleClass="p-selectbutton-sm"
                  size="small"
                ></p-selectButton>
                <lucide-angular
                  [img]="infoIcon"
                  class="h-4 w-4 cursor-help text-greyscale-500"
                  pTooltip="{{ t('impact.viewMode.tooltip') }}"
                  tooltipPosition="top"
                ></lucide-angular>
              </div>
            </div>
            <button
              class="rounded-lg p-1 text-sm text-greyscale-500 transition-colors hover:bg-greyscale-50 hover:text-greyscale-700"
              (click)="toggleAllSections()"
              [pTooltip]="'impactReport.page.expandCollapse' | transloco"
              tooltipPosition="top"
            >
              <lucide-angular
                [img]="allSectionsOpen ? chevronsUpIcon : chevronsDownIcon"
                class="h-4 w-4"
              >
              </lucide-angular>
            </button>
          </div>

          <div
            class="space-y-0 overflow-hidden rounded-lg border border-greyscale-100"
          >
            <app-collapsible-section
              [title]="t('impact.categories.transport')"
              categoryType="transport"
              [isOpen]="allSectionsOpen"
              [chartData]="getTransportChartData()"
              [showPercentage]="showPercentage"
              [hasTimData]="hasTimData"
            >
            </app-collapsible-section>

            <app-collapsible-section
              [title]="t('impact.categories.accommodation')"
              categoryType="accommodation"
              [isOpen]="allSectionsOpen"
              [chartData]="getAccommodationChartData()"
              [showPercentage]="showPercentage"
            >
            </app-collapsible-section>

            <app-collapsible-section
              [title]="t('impact.categories.activities')"
              categoryType="activities"
              [isOpen]="allSectionsOpen"
              [chartData]="getActivitiesChartData()"
              [showPercentage]="showPercentage"
            >
            </app-collapsible-section>

            <app-collapsible-section
              [title]="t('impact.categories.food')"
              categoryType="food"
              [isOpen]="allSectionsOpen"
              [chartData]="getFoodChartData()"
              [showPercentage]="showPercentage"
            >
            </app-collapsible-section>

            <app-collapsible-section
              [title]="t('impact.categories.dailyImpact')"
              categoryType="daily"
              [isOpen]="allSectionsOpen"
              [chartData]="[]"
              [showPercentage]="showPercentage"
            >
              <app-impact-per-day-chart
                [dailyData]="dailyImpactData"
                [hasScheduledElements]="hasScheduledElements"
                [showPercentage]="showPercentage"
              >
              </app-impact-per-day-chart>
            </app-collapsible-section>
            <app-eco-score
              [isOpen]="allSectionsOpen"
              [carbonIntensity]="
                impactReport.trip.tripImpact?.carbonIntensity ?? 0
              "
            />
          </div>
        </section>

        <!-- ADEME Équivalences -->
        @if (impactReport.ademeEquivalences && impactReport.impactCo2Meta) {
          <div class="mt-8">
            <app-ademe-equivalences
              [equivalences]="impactReport.ademeEquivalences"
              [meta]="impactReport.impactCo2Meta"
            >
            </app-ademe-equivalences>
          </div>
        }

        <!-- CTA Contribution -->
        <div class="mt-8">
          <app-contribution-cta
            #contributionCta
            [tripId]="impactReport.trip.id"
            [tripName]="impactReport.trip.title || ''"
            [co2Impact]="impactReport.trip.tripImpact?.impactPerTraveller || 0"
          >
          </app-contribution-cta>
        </div>
      </div>
    </div>
  </div>
} @else {
  <!-- Loading state -->
  <div class="flex h-[calc(100dvh-200px)] items-center justify-center">
    <app-greentripper-loader size="xl"></app-greentripper-loader>
  </div>
}
